import React, {useMemo, useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import ExperimentUtils, {IMMEDIATE_FEATURES} from 'utils/experiment';
import {
    Text,
    Flex,
    Button,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from '@spothero/ui';
import useConfig from 'hooks/use-config';
import dayjs from 'utils/dayjs-timezone';
import {PROPTYPE_RESERVATION} from '@/proptypes/index';
import {PROPTYPE_RESERVATION_PAYMENT_STATUS} from '@/proptypes/reservation-prop-types';
import ReservationUtils from 'utils/reservation-utils';
import IconInfo from '@spothero/icons/info';
import {useDispatch} from 'react-redux';
import {toggleModal} from 'store/parking-pass/parking-pass-actions';
import {CHANGE_TIMES} from 'store/parking-pass/parking-pass-reducer';
import ChangeTimesModal from '../change-times-modal';

const getDateStrings = (datetime, timezone) => {
    const formattedDate = datetime.format('ddd, MMMM D');
    let dateForDisplay = formattedDate;
    const currentDateTime = dayjs.utc(dayjs()).tz(timezone);
    const tomorrowDateTime = currentDateTime.add(1, 'day');

    if (datetime.isSame(currentDateTime, 'day')) {
        dateForDisplay = 'Today';
    } else if (datetime.isSame(tomorrowDateTime, 'day')) {
        dateForDisplay = 'Tomorrow';
    }

    return {default: dateForDisplay, print: formattedDate};
};

export const timeBoxStyles = {
    flexDirection: 'column',
    flex: 1,
    alignItems: 'start',
    padding: 4,
};

export const labelStyles = {
    color: 'text.secondary.light',
    fontSize: 'sm',
    marginBottom: 1,
    lineHeight: 1.4,
};

export const dateTimeContainerStyles = {
    justifyContent: 'center',
    borderBottomWidth: '1px',
    borderColor: 'gray.100',
};

const timeStyles = {
    fontSize: {base: 'lg', tablet: 'xl'},
    fontWeight: {base: 'bold', tablet: 'semibold'},
    lineHeight: 1.4,
};

const dateStyles = {
    fontSize: 'base',
    lineHeight: 1.4,
};

const editStyles = {
    lineHeight: 1.4,
    marginTop: 1,
};

const hiddenOnPrint = {
    sx: {
        '@media print': {display: 'none'},
    },
};

const showOnlyOnPrint = {
    display: 'none',
    sx: {
        '@media print': {display: 'initial'},
    },
};

const DateTimeDetails = ({
    reservation,
    activeStatus,
    paymentStatus,
    onTrackedClick,
    enteredAndCantChange,
}) => {
    const {siteUrl} = useConfig();
    const [showModal, setShowModal] = useState(false);
    const [
        userInChangeTimeExperiment,
        setUserInChangeTimeExperiment,
    ] = useState(false);
    const dispatch = useDispatch();
    const toggleInfoModal = () => setShowModal(prevShowModal => !prevShowModal);
    const {canUpdateStartsAndEnds} = reservation;

    const startTime = useMemo(() => reservation.starts.format('h:mm A'), [
        reservation.starts,
    ]);
    const startDate = useMemo(
        () => getDateStrings(reservation.starts, reservation.timezone),
        [reservation.starts, reservation.timezone]
    );
    const endTime = useMemo(() => reservation.ends.format('h:mm A'), [
        reservation.ends,
    ]);
    const endDate = useMemo(
        () => getDateStrings(reservation.ends, reservation.timezone),
        [reservation.ends, reservation.timezone]
    );
    const noPartnerRestrictions =
        !reservation.partner || reservation.partner?.updateTimesEnabled;

    const editLink = `${siteUrl}/account-reservations#current-${reservation.rentalId}-change`;
    const showEditEndTime =
        activeStatus !== ReservationUtils.ACTIVE_STATUS.ENDED &&
        paymentStatus !== ReservationUtils.PAYMENT_STATUSES.REFUND &&
        canUpdateStartsAndEnds &&
        noPartnerRestrictions;
    const showEditStartTime =
        activeStatus === ReservationUtils.ACTIVE_STATUS.NOT_STARTED &&
        paymentStatus !== ReservationUtils.PAYMENT_STATUSES.REFUND &&
        canUpdateStartsAndEnds &&
        noPartnerRestrictions;

    const handleToggleModal = () => {
        dispatch(toggleModal({modal: CHANGE_TIMES}));
    };

    // Put in useEffect so no hydration errors
    useEffect(() => {
        if (
            ExperimentUtils.isFeatureEnabled(
                IMMEDIATE_FEATURES.PARKING_PASS_TIME_ADJUSTMENTS
            )
        ) {
            setUserInChangeTimeExperiment(true);
        }
    }, []);

    const changeButtonProps = userInChangeTimeExperiment
        ? {
              as: 'a',
              href: '#',
              onClick: handleToggleModal,
          }
        : {
              as: 'a',
              href: editLink,
          };

    const changeEndTimeButton = () => {
        if (enteredAndCantChange) {
            return (
                <Button
                    {...editStyles}
                    color="gray.dark"
                    _hover={{
                        color: 'gray.dark',
                    }}
                    onClick={toggleInfoModal}
                    data-testid="end-time-disabled-edit-button"
                    variant="tertiary"
                    {...hiddenOnPrint}
                >
                    Change
                </Button>
            );
        } else {
            return (
                <Button
                    {...editStyles}
                    {...changeButtonProps}
                    onClick={() => {
                        if (changeButtonProps.onClick) {
                            changeButtonProps.onClick();
                        }
                        onTrackedClick({
                            clickTarget: 'Change End Time',
                            isReservationChange: true,
                        });
                    }}
                    data-testid="end-time-edit-button"
                    variant="tertiary"
                    {...hiddenOnPrint}
                >
                    Change
                </Button>
            );
        }
    };

    return (
        <>
            <Flex {...dateTimeContainerStyles}>
                <Flex {...timeBoxStyles}>
                    <Text {...labelStyles}>Enter After</Text>
                    <Text data-testid="start-time" {...timeStyles}>
                        {startTime}
                    </Text>
                    <Text
                        data-testid="start-date"
                        {...dateStyles}
                        {...hiddenOnPrint}
                    >
                        {startDate.default}
                    </Text>

                    <Text {...dateStyles} {...showOnlyOnPrint}>
                        {startDate.print}
                    </Text>
                    {showEditStartTime && (
                        <Button
                            {...editStyles}
                            {...changeButtonProps}
                            onClick={() => {
                                if (changeButtonProps.onClick) {
                                    changeButtonProps.onClick();
                                }
                                onTrackedClick({
                                    clickTarget: 'Change Start Time',
                                    isReservationChange: true,
                                });
                            }}
                            data-testid="start-time-edit-button"
                            variant="tertiary"
                            {...hiddenOnPrint}
                        >
                            Change
                        </Button>
                    )}
                </Flex>
                <Flex
                    borderLeftWidth="1px"
                    borderColor="gray.100"
                    {...timeBoxStyles}
                >
                    <Text {...labelStyles}>Exit Before</Text>
                    <Text data-testid="end-time" {...timeStyles}>
                        {endTime}
                    </Text>
                    <Text
                        data-testid="end-date"
                        {...dateStyles}
                        {...hiddenOnPrint}
                    >
                        {endDate.default}
                    </Text>
                    <Text {...dateStyles} {...showOnlyOnPrint}>
                        {endDate.print}
                    </Text>
                    {!canUpdateStartsAndEnds && noPartnerRestrictions && (
                        <Button
                            variant="tertiary"
                            onClick={toggleInfoModal}
                            rightIcon={<IconInfo height="18px" width="18px" />}
                            iconSpacing={1.5}
                            textTransform="auto"
                            color="text.secondary.light"
                            data-testid="DateTimeDetails-button-no-extensions-info"
                        >
                            <Text>No Extensions</Text>
                        </Button>
                    )}
                    {showEditEndTime && changeEndTimeButton()}
                </Flex>
            </Flex>

            <Modal
                hasHeader
                hasFooter
                size="sm"
                isOpen={showModal}
                onClose={toggleInfoModal}
                data-testid="DateTimeDetails-modal-no-extensions-info"
            >
                <ModalHeader fontSize="lg">
                    {enteredAndCantChange
                        ? 'No changes after entry'
                        : 'No extensions at this facility'}
                </ModalHeader>
                <ModalBody>
                    <Text>
                        <strong>
                            {enteredAndCantChange
                                ? 'Time & vehicle changes are not allowed at this facility after entry.'
                                : 'Time adjustments are not allowed at this facility after booking.'}
                        </strong>{' '}
                        You won&apos;t be refunded for leaving early and you may
                        be charged extra if you leave late.
                    </Text>
                </ModalBody>
                <ModalFooter>
                    <Button
                        onClick={toggleInfoModal}
                        variant="primary"
                        width="100%"
                    >
                        Okay
                    </Button>
                </ModalFooter>
            </Modal>

            {showEditEndTime && userInChangeTimeExperiment ? (
                <ChangeTimesModal
                    canEditTimes={showEditEndTime}
                    reservation={reservation}
                    activeStatus={activeStatus}
                />
            ) : null}
        </>
    );
};

DateTimeDetails.propTypes = {
    reservation: PROPTYPE_RESERVATION,
    activeStatus: PropTypes.oneOf(
        Object.values(ReservationUtils.ACTIVE_STATUS)
    ),
    paymentStatus: PROPTYPE_RESERVATION_PAYMENT_STATUS,
    onTrackedClick: PropTypes.func.isRequired,
    enteredAndCantChange: PropTypes.bool,
};

export default DateTimeDetails;
